import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from "../layouts/DashboardLayout";
import { getDashboardInfo } from "../../../mobile/api/MemberApi";

const SystemPage = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const mapRef = useRef(null);
    const [map, setMap] = useState(null);
    const infoWindowsRef = useRef([]);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    useEffect(() => {
        if (dashboardData && window.kakao && window.kakao.maps) {
            initializeMap();
        }
    }, [dashboardData]);

    const fetchDashboardData = async () => {
        try {
            const response = await getDashboardInfo();
            setDashboardData(response);
        } catch (error) {
            console.error('대시보드 데이터 조회 실패:', error);
            alert('대시보드 데이터를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const isValidLocation = (latitude, longitude) => {
        return latitude !== null && 
               latitude !== undefined && 
               longitude !== null && 
               longitude !== undefined && 
               typeof latitude === 'number' && 
               typeof longitude === 'number' &&
               !isNaN(latitude) && 
               !isNaN(longitude);
    };

    const closeAllInfoWindows = () => {
        infoWindowsRef.current.forEach(infoWindow => {
            infoWindow.close();
        });
    };

    const initializeMap = () => {
        if (!mapRef.current) return;

        const kakao = window.kakao;
        
        // 인포윈도우 배열 초기화
        infoWindowsRef.current = [];
        
        // 지도 중심점 설정 (충주시청 좌표)
        const center = new kakao.maps.LatLng(36.9723188, 127.9330595);
        
        const mapOption = {
            center: center,
            level: 13 // 대한민국이 다 보이는 레벨
        };

        // 지도 생성
        const mapInstance = new kakao.maps.Map(mapRef.current, mapOption);
        setMap(mapInstance);

        // 지도 클릭 이벤트 - 모든 인포윈도우 닫기
        kakao.maps.event.addListener(mapInstance, 'click', function() {
            closeAllInfoWindows();
        });

        // 회원 위치 마커 (파란색)
        if (dashboardData?.memberLocationDtoList) {
            dashboardData.memberLocationDtoList.forEach(member => {
                if (isValidLocation(member.latitude, member.longitude)) {
                    const memberPosition = new kakao.maps.LatLng(member.latitude, member.longitude);
                    
                    const memberMarker = new kakao.maps.Marker({
                        position: memberPosition,
                        map: mapInstance,
                    });

                    // 인포윈도우 생성
                    const memberInfoWindow = new kakao.maps.InfoWindow({
                        content: `<div style="padding:5px;font-size:12px;">회원 ID: ${member.memberId}</div>`
                    });

                    // 인포윈도우를 배열에 추가
                    infoWindowsRef.current.push(memberInfoWindow);

                    // 마커 클릭 이벤트
                    kakao.maps.event.addListener(memberMarker, 'click', function(mouseEvent) {
                        // 다른 인포윈도우들 먼저 닫기
                        closeAllInfoWindows();
                        // 현재 인포윈도우 열기
                        memberInfoWindow.open(mapInstance, memberMarker);
                        // 이벤트 전파 중단 (지도 클릭 이벤트 방지)
                        if (mouseEvent && mouseEvent.stop) mouseEvent.stop();
                    });
                }
            });
        }

        // 모임 위치 마커 (별 모양)
        if (dashboardData?.meetingLocationDtoList) {
            dashboardData.meetingLocationDtoList.forEach(meeting => {
                if (isValidLocation(meeting.latitude, meeting.longitude)) {
                    const meetingPosition = new kakao.maps.LatLng(meeting.latitude, meeting.longitude);
                    
                    const meetingMarker = new kakao.maps.Marker({
                        position: meetingPosition,
                        map: mapInstance,
                        image: new kakao.maps.MarkerImage(
                            'https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/markerStar.png',
                            new kakao.maps.Size(24, 35)
                        )
                    });

                    // 인포윈도우 생성
                    const meetingInfoWindow = new kakao.maps.InfoWindow({
                        content: `<div style="padding:5px;font-size:12px;">모임 ID: ${meeting.meetingId}</div>`
                    });

                    // 인포윈도우를 배열에 추가
                    infoWindowsRef.current.push(meetingInfoWindow);

                    // 마커 클릭 이벤트
                    kakao.maps.event.addListener(meetingMarker, 'click', function(mouseEvent) {
                        // 다른 인포윈도우들 먼저 닫기
                        closeAllInfoWindows();
                        // 현재 인포윈도우 열기
                        meetingInfoWindow.open(mapInstance, meetingMarker);
                        // 이벤트 전파 중단 (지도 클릭 이벤트 방지)
                        if (mouseEvent && mouseEvent.stop) mouseEvent.stop();
                    });
                }
            });
        }
    };

    const getValidLocationCount = (locationList) => {
        return locationList ? locationList.filter(location => 
            isValidLocation(location.latitude, location.longitude)
        ).length : 0;
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="p-2">
                    <h1 className="text-lg font-bold mb-2">시스템 현황</h1>
                    <div className="text-center py-8">대시보드 데이터를 불러오는 중...</div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="p-3">
                <h1 className="text-lg font-bold mb-3">시스템 현황</h1>
                
                {/* 시스템 지표 */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-blue-100 border border-blue-300 p-2 text-center">
                        <h2 className="text-xs font-bold text-blue-800 mb-1">총 회원 수</h2>
                        <div className="text-xl font-bold text-blue-900">
                            {dashboardData?.memberCount || 0}
                        </div>
                    </div>
                    <div className="bg-yellow-100 border border-yellow-300 p-2 text-center">
                        <h2 className="text-xs font-bold text-yellow-800 mb-1">총 모임 수</h2>
                        <div className="text-xl font-bold text-yellow-900">
                            {dashboardData?.meetingCount || 0}
                        </div>
                    </div>
                </div>

                {/* 범례 */}
                <div className="mb-2 p-2 bg-gray-100 border border-gray-300">
                    <div className="flex gap-4 text-xs">
                        <div className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-blue-500"></div>
                            <span>회원 ({getValidLocationCount(dashboardData?.memberLocationDtoList)})</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-yellow-500"></div>
                            <span>모임 ({getValidLocationCount(dashboardData?.meetingLocationDtoList)})</span>
                        </div>
                    </div>
                </div>

                {/* 카카오맵 */}
                <div className="border border-gray-300">
                    <div 
                        ref={mapRef}
                        className="w-full"
                        style={{ height: '600px' }}
                    />
                </div>
            </div>
        </DashboardLayout>
    );
};

export default SystemPage; 